'use client';

import clsx from 'clsx';
import { type ForwardedRef, useImperativeHandle, useRef } from 'react';
import {
  TextArea as RACTextArea,
  TextField as RACTextField,
  type TextAreaProps as RACTextAreaProps,
  type TextFieldProps as RACTextFieldProps,
  Group as RACGroup,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import type { ApiProps, CommonProps, FieldWithPlaceholderProps } from '../types.js';
import { textStyle } from '../typography/utils.js';

export interface TextAreaProps
  extends
    CommonProps,
    FieldWithPlaceholderProps<string>,
    ApiProps<TextAreaApi>,
    Pick<
      RACTextFieldProps,
      | 'value'
      | 'defaultValue'
      | 'onChange'
      | 'isDisabled'
      | 'isRequired'
      | 'isInvalid'
      | 'isReadOnly'
      | 'type'
      | 'autoFocus'
      | 'autoComplete'
      | 'inputMode'
      | 'minLength'
      | 'maxLength'
      | 'onFocus'
      | 'onBlur'
      | 'onKeyDown'
    > {
  /** Controls whether inputted text is automatically capitalized. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/autocapitalize). */
  autoCapitalize?: RACTextAreaProps['autoCapitalize'];
  /** Controls whether inputted text can be autocorrected by the browser. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/autocorrect). */
  autoCorrect?: 'on' | 'off';
  /** Controls how many lines of text are visible by default. See [MDN](https://developer.mozilla.org/en-US/docs/Web/API/HTMLTextAreaElement/rows). */
  rows?: number;
  /** Controls whether the inputted text may be checked for spelling errors. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/spellcheck). */
  spellCheck?: boolean;
  /**
   * Controls how the inputted text will be wrapped for form submission. See [MDN](https://developer.mozilla.org/en-US/docs/Web/API/HTMLTextAreaElement/wrap).
   * @default 'soft'
   */
  wrap?: 'soft' | 'hard';
}

/** The imperative API exposed by the `TextArea` component. */
export interface TextAreaApi {
  /** Focuses the text area. */
  focus: () => void;
  /** Selects the contents of the text area. */
  select: () => void;
}

function TextArea(
  {
    label,
    description,
    error: errorMessage,
    placeholder,
    autoCapitalize,
    autoCorrect,
    rows,
    spellCheck,
    wrap,
    UNSAFE_className,
    UNSAFE_style,
    apiRef,
    ...props
  }: TextAreaProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('TextArea requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  useImperativeHandle(
    apiRef,
    () => ({
      focus: () => textAreaRef.current?.focus(),
      select: () => textAreaRef.current?.select(),
    }),
    [],
  );

  return (
    <RACTextField {...props} ref={ref} className={clsx('cim-text-area', UNSAFE_className)} style={UNSAFE_style}>
      {({ isDisabled, isRequired }) => (
        <>
          <FormFieldLabel isRequired={isRequired} isDisabled={isDisabled}>
            {label}
          </FormFieldLabel>
          <RACGroup className="cim-text-area-input-group" onClick={() => textAreaRef.current?.focus()}>
            <RACTextArea
              ref={textAreaRef}
              className={clsx('cim-text-area-input', textStyle({ variant: 'body' }))}
              placeholder={placeholder}
              autoCapitalize={autoCapitalize}
              autoCorrect={autoCorrect}
              rows={rows}
              spellCheck={spellCheck}
              wrap={wrap}
            />
          </RACGroup>
          <FormFieldError>{errorMessage}</FormFieldError>
          <FormFieldDescription>{description}</FormFieldDescription>
        </>
      )}
    </RACTextField>
  );
}

/**
 * Allows users to enter multiple lines of text with a keyboard.
 *
 * See [text area usage guidelines](https://ui.cimpress.io/components/text-area/).
 */
const _TextArea = withStyleProps(forwardRef(TextArea), 'TextArea');

export { _TextArea as TextArea };
